import { AllergyIntolerance, Patient, ValueSetExpansionContains } from '@medplum/fhirtypes'
import { ChartItem } from './PatientChart.utils.tsx'
import { Box, Select, TextInput } from '@mantine/core'
import { ResourceTable } from '@/pages/Patient/components/ResourceTable.tsx'
import { formatDateTime } from '@/utils.ts'
import { useState } from 'react'
import { ValueSetAutocomplete } from '@medplum/react'
import { AddResourceSection } from './AddResourceSection.tsx'

type Props = {
  patient: Patient
  item: ChartItem
}

const availFields = [
  { code: 'code', title: 'Substance' },
  { code: 'reaction', title: 'Reaction' },
  { code: 'clinical-status', title: 'Status' },
  { code: 'date', title: 'Documented' },
  { code: 'started', title: 'Started' },
  { code: 'completed', title: 'Completed' },
  { code: 'nonDrug', title: 'Non Drug' },
]

const RXNORM_ALLERGENS_VALUE_SET =
  'http://cts.nlm.nih.gov/fhir/ValueSet/2.16.840.1.113762.1.4.1186.8'

const CLINICAL_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'resolved', label: 'Resolved' },
]

const SEVERITY_OPTIONS = [
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
]

const CATEGORY_OPTIONS = [
  { value: 'medication', label: 'Medication' },
  { value: 'food', label: 'Food' },
  { value: 'environment', label: 'Environment' },
  { value: 'biologic', label: 'Biologic' },
]

const renderColumn = (resource: AllergyIntolerance, itemCode: string): string => {
  switch (itemCode) {
    case 'code':
      return resource.code?.text || resource.code?.coding?.[0]?.display || 'NO KNOWN DRUG ALLERGIES'
    case 'reaction': {
      if (!resource.reaction?.length) return ''
      return resource.reaction
        .map((r) =>
          r.manifestation
            ?.map((m) => m.text || m.coding?.[0]?.display || '')
            .filter(Boolean)
            .join(', '),
        )
        .filter(Boolean)
        .join('; ')
    }
    case 'clinical-status': {
      // Capitalize on read — writers store the status with inconsistent casing
      // (the PN cascade writes "Active", the add-allergy modal wrote "active").
      const status =
        resource.clinicalStatus?.text ||
        resource.verificationStatus?.text ||
        resource.clinicalStatus?.coding?.[0]?.display ||
        resource.verificationStatus?.coding?.[0]?.display ||
        ''
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'
    }
    case 'date':
      return resource.recordedDate ? formatDateTime(resource.recordedDate) : ''
    case 'started': {
      const result =
        resource.onsetDateTime ||
        resource.onsetPeriod?.start ||
        resource.onsetRange?.low?.value?.toString() ||
        resource.onsetString ||
        ''

      return result ? formatDateTime(result) : result
    }
    case 'completed':
      return resource.lastOccurrence || ''
    case 'nonDrug':
      return resource.category?.includes('medication') ? '' : 'Yes'
    default:
      return ''
  }
}

export const Allergies = ({ patient, item }: Props) => {
  const [picked, setPicked] = useState<{ system?: string; code: string; display: string } | null>(
    null,
  )
  const [pickerKey, setPickerKey] = useState(0)
  const [category, setCategory] = useState<string | null>('medication')
  const [clinicalStatus, setClinicalStatus] = useState<string | null>('active')
  const [severity, setSeverity] = useState<string | null>(null)
  const [reaction, setReaction] = useState('')

  const resetForm = () => {
    setPicked(null)
    setCategory('medication')
    setClinicalStatus('active')
    setSeverity(null)
    setReaction('')
    setPickerKey((k) => k + 1)
  }

  const handlePickerChange = (items: ValueSetExpansionContains[]) => {
    const first = items[0]
    if (!first?.code) {
      setPicked(null)
      return
    }
    setPicked({
      system: first.system,
      code: first.code,
      display: first.display || first.code,
    })
  }

  const buildResource = (): AllergyIntolerance | { error: string } => {
    if (!picked?.code) return { error: 'Please select an allergen.' }
    return {
      resourceType: 'AllergyIntolerance',
      patient: { reference: `Patient/${patient.id}` },
      clinicalStatus: clinicalStatus
        ? {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
                code: clinicalStatus,
                display: clinicalStatus.charAt(0).toUpperCase() + clinicalStatus.slice(1),
              },
            ],
          }
        : undefined,
      verificationStatus: {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
            code: 'confirmed',
            display: 'Confirmed',
          },
        ],
      },
      category: category ? ([category] as AllergyIntolerance['category']) : undefined,
      code: {
        coding: [
          {
            system: picked.system,
            code: picked.code,
            display: picked.display,
          },
        ],
        text: picked.display,
      },
      recordedDate: new Date().toISOString(),
      reaction: reaction.trim()
        ? [
            {
              manifestation: [{ text: reaction.trim() }],
              severity: (severity as 'mild' | 'moderate' | 'severe' | null) || undefined,
            },
          ]
        : undefined,
    }
  }

  return (
    <AddResourceSection<AllergyIntolerance>
      patient={patient}
      title="Allergies"
      modalTitle="Add Allergy"
      resourceType="AllergyIntolerance"
      featureContext="AllergyIntolerance"
      noun="allergy"
      isSubmitDisabled={!picked?.code}
      resetForm={resetForm}
      buildResource={buildResource}
      scrollMode="window"
      renderForm={() => (
        <>
          <Select
            label="Category"
            data={CATEGORY_OPTIONS}
            value={category}
            onChange={setCategory}
          />
          <Box>
            <p className="mb-1 text-sm font-medium">Allergen *</p>
            <ValueSetAutocomplete
              key={pickerKey}
              name="add-allergy-substance"
              placeholder="Search allergen (RxNorm)"
              binding={RXNORM_ALLERGENS_VALUE_SET}
              maxValues={1}
              clearable={false}
              onChange={handlePickerChange}
            />
            {picked && (
              <p className="mt-1 text-xs text-gray-500">
                Selected: {picked.display} ({picked.code})
              </p>
            )}
          </Box>
          <Select
            label="Clinical Status"
            data={CLINICAL_STATUS_OPTIONS}
            value={clinicalStatus}
            onChange={setClinicalStatus}
          />
          <TextInput
            label="Reaction"
            placeholder="e.g. Hives, Anaphylaxis"
            value={reaction}
            onChange={(e) => setReaction(e.currentTarget.value)}
          />
          <Select
            label="Severity"
            data={SEVERITY_OPTIONS}
            value={severity}
            onChange={setSeverity}
            clearable
          />
        </>
      )}
    >
      {(refreshKey) => (
        <ResourceTable
          key={refreshKey}
          patient={patient}
          item={item}
          availFields={availFields}
          renderColumn={renderColumn}
          defaultSort="date"
          blockRemove
        />
      )}
    </AddResourceSection>
  )
}
